import { useState } from "react";
import type { Me } from "../../lib/me";
import { RoleButton } from "../RequireRole";
import { acceptChoices, agreementOf, boxStyle, REVIEW_TONE } from "./flow";
import { pidUrls, type Reading, type ReviewAction, type SheetDetail } from "./pidApi";

const SHOWN_WIDTH = 1600;

/** The page with a box on every reading. A box is outlined by its reader agreement and is a button. */
export function PageView({ detail, selected, onSelect }: {
  detail: SheetDetail;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const w = detail.width_px ?? 1;
  const h = detail.height_px ?? 1;
  return (
    <div className="card" style={{ padding: 8 }}>
      <div style={{ position: "relative", background: "var(--color-paper)", aspectRatio: `${w} / ${h}` }} data-pid-page>
        <img
          src={pidUrls.image(detail.sheet.id, SHOWN_WIDTH)}
          alt={`${detail.sheet.filename}, page ${detail.sheet.page}`}
          style={{ width: "100%", height: "100%", display: "block", opacity: loaded ? 1 : 0.3 }}
          onLoad={() => setLoaded(true)}
        />
        {detail.readings.map((r) => {
          const tone = r.review_state === "unreviewed" ? agreementOf(r.agreement).tone : REVIEW_TONE[r.review_state];
          const active = selected === r.id;
          return (
            <button
              key={r.id}
              type="button"
              data-reading-box={r.id}
              aria-label={`${r.text || "unreadable"}, ${agreementOf(r.agreement).label}, ${r.review_state}`}
              aria-pressed={active}
              onClick={() => onSelect(r.id)}
              style={{
                position: "absolute", ...boxStyle(r.box, w, h), padding: 0, minWidth: 0,
                background: active ? "var(--color-citation)" : "transparent",
                border: `2px solid var(--color-${tone})`, borderRadius: 2, cursor: "pointer",
                opacity: r.review_state === "rejected" ? 0.4 : 1,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

/** One reading: the crop, what each reader saw, its tags, and the review actions. */
export function ReadingPanel({ reading, me, busy, error, onReview }: {
  reading: Reading;
  me: Me | null | undefined;
  busy: boolean;
  error: string | null;
  onReview: (action: ReviewAction, value?: string, reason?: string) => void;
}) {
  const [value, setValue] = useState(reading.final_text ?? reading.text);
  const [reason, setReason] = useState("");
  const agreement = agreementOf(reading.agreement);
  const choices = acceptChoices(reading);
  const reviewed = reading.review_state !== "unreviewed";
  const freeOnly = reading.agreement === "free_reader_only";
  return (
    <section className="card stack" style={{ padding: 16, gap: 12 }} data-pid-reading={reading.id}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <span className="mono" style={{ fontSize: 18, color: "var(--color-primary)" }}>{reading.final_text ?? (reading.text || "(nothing read)")}</span>
        <span className={`pill ${REVIEW_TONE[reading.review_state] ?? "info"}`}>{reading.review_state}</span>
      </div>
      <img src={pidUrls.crop(reading.id)} alt={`Crop of ${reading.text || "the reading"}`}
        style={{ maxWidth: "100%", maxHeight: 220, objectFit: "contain", border: "1px solid var(--color-border)", background: "var(--color-paper)" }} />
      <dl style={{ fontSize: 13, display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 12px", alignItems: "baseline" }}>
        <dt className="muted">Kind</dt><dd>{reading.kind.replace(/_/g, " ")}</dd>
        <dt className="muted">Model read</dt>
        <dd className="mono">
          {freeOnly ? <span className="muted">not read by the model</span> : `${reading.text || "-"}${reading.suffix ? ` (suffix ${reading.suffix})` : ""}`}
        </dd>
        <dt className="muted">Free reader</dt><dd className="mono">{reading.second_text || "-"}</dd>
        <dt className="muted">Agreement</dt><dd><span className={`pill ${agreement.tone}`}>{agreement.label}</span></dd>
        {reading.alternates.length > 0 && (<><dt className="muted">Look-alikes</dt><dd className="mono">{reading.alternates.join(", ")}</dd></>)}
        <dt className="muted">Tags</dt>
        <dd>
          {reading.tags.length === 0 && <span className="muted">none</span>}
          {reading.tags.map((t, i) => (
            <span key={i} className={`pill ${t.status === "ok" ? "ok" : "review"}`} title={String(t.reason ?? "")} style={{ marginRight: 4 }}>
              {String(t.normalized || "unparsed")}
            </span>
          ))}
        </dd>
      </dl>

      {error && <p role="alert" style={{ color: "var(--color-fail)", fontSize: 13 }}>{error}</p>}

      {!reviewed ? (
        <div className="stack" style={{ gap: 8 }}>
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            {choices.map((c) => (
              <RoleButton key={c} role="engineer" me={me} disabled={busy} onClick={() => onReview("accept", c)}>
                Accept {c}
              </RoleButton>
            ))}
          </div>
          <label className="muted" style={{ fontSize: 12 }} htmlFor={`edit-${reading.id}`}>Correct value</label>
          <input id={`edit-${reading.id}`} value={value} onChange={(e) => setValue(e.target.value)} className="mono" />
          <label className="muted" style={{ fontSize: 12 }} htmlFor={`why-${reading.id}`}>Reason (needed to edit or reject)</label>
          <input id={`why-${reading.id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="checked on the loop diagram" />
          <div className="row" style={{ gap: 6 }}>
            <RoleButton role="engineer" me={me} className="ghost" disabled={busy || !value.trim() || !reason.trim()}
              onClick={() => onReview("edit", value, reason)}>Save edit</RoleButton>
            <RoleButton role="engineer" me={me} className="ghost" disabled={busy || !reason.trim()}
              onClick={() => onReview("reject", undefined, reason)}>Reject</RoleButton>
          </div>
        </div>
      ) : (
        <RoleButton role="engineer" me={me} className="ghost" disabled={busy} onClick={() => onReview("reopen")}>Reopen</RoleButton>
      )}

      {reading.history.length > 0 && (
        <ul className="muted" style={{ fontSize: 12, marginLeft: 16 }} aria-label="review history">
          {reading.history.map((h, i) => (
            <li key={i}>{String(h.action)} by {String(h.by)}{h.reason ? `: ${String(h.reason)}` : ""}</li>
          ))}
        </ul>
      )}
    </section>
  );
}
